<template>
  <Teleport to="body">
    <!-- Overlay sombre -->
    <Transition name="overlay">
      <div 
        v-if="isMenuOpen"
        class="fixed inset-0 bg-black z-40 overlay-backdrop"
        :class="overlayClasses"
        @click="handleClose"
      />
    </Transition>
    
    <!-- Drawer -->
    <Transition name="drawer">
      <div 
        v-if="isMenuOpen"
        :class="drawerClasses"
        class="mobile-menu-drawer"
        @click.stop
      >
        <MobileMenuHeader
          :logo-props="logoProps"
          :is-menu-open="isMenuOpen"
          @close="handleClose"
        />
        
        <MobileMenuNavigation
          :menu-items="menuItems"
          :active-submenu="activeSubmenu"
          @submenu-toggle="handleSubmenuToggle"
        />
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import MobileMenuHeader from './MobileMenuHeader.vue'
import MobileMenuNavigation from './MobileMenuNavigation.vue'
import { cn } from '@/lib/utils'
import type { MobileMenuItem } from '../types/mobileNavigation'

interface Props {
  isMenuOpen: boolean
  activeSubmenu: string | null
  menuItems: MobileMenuItem[]
  logoProps?: any
}

defineProps<Props>()

const emit = defineEmits<{
  close: []
  submenuToggle: [menuKey: string, hasSubmenu: boolean]
}>()

const overlayClasses = computed(() => 
  'transition-opacity duration-300'
)

const drawerClasses = computed(() => 
  cn(
    'fixed top-0 right-0 h-full w-full bg-white shadow-2xl z-50 overflow-y-auto'
  )
)

const handleClose = () => {
  emit('close')
}

const handleSubmenuToggle = (menuKey: string, hasSubmenu: boolean) => {
  console.log('🔄 [MobileMenuDrawer] handleSubmenuToggle received:', { menuKey, hasSubmenu })
  emit('submenuToggle', menuKey, hasSubmenu)
}
</script>

<style scoped>
/* Animation de l'overlay */
.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

.overlay-enter-to,
.overlay-leave-from {
  opacity: 0.5;
}

/* Animation du drawer */
.drawer-enter-active {
  transition: transform 0.4s cubic-bezier(0.4, 0.0, 0.2, 1), 
              opacity 0.4s cubic-bezier(0.4, 0.0, 0.2, 1),
              box-shadow 0.4s cubic-bezier(0.4, 0.0, 0.2, 1);
}

.drawer-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0.0, 0.6, 1), 
              opacity 0.3s cubic-bezier(0.4, 0.0, 0.6, 1),
              box-shadow 0.3s cubic-bezier(0.4, 0.0, 0.6, 1);
}

.drawer-enter-from {
  transform: translateX(100%);
  opacity: 0;
  box-shadow: 0 0 0 rgba(0, 0, 0, 0);
}

.drawer-enter-to {
  transform: translateX(0);
  opacity: 1;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
}

.drawer-leave-from {
  transform: translateX(0);
  opacity: 1;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
}

.drawer-leave-to {
  transform: translateX(100%);
  opacity: 0;
  box-shadow: 0 0 0 rgba(0, 0, 0, 0);
}
</style>

